import React, { useState, useEffect } from "react";
import "./GridMediaLayout.css";
import { useVideoManager } from "../context/VideoManager";


const GridMediaLayout = ({
  mediaItems,
  rows = 2,
  columns = 2,
  paddingLeft = 0,
  paddingRight = 0,
  gap = 0,
  useDynamicPadding = false, // New: Enable dynamic padding
  maxPadding = 300, // New: Maximum padding for larger screens
  disablePaddingOnMobile = false, // New: Disable padding for small screens
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [dynamicPadding, setDynamicPadding] = useState({
    paddingLeft,
    paddingRight,
  });

  const { handleVideoUnmute } = useVideoManager();

  const handleMuteToggle = (videoRef) => {
    if (videoRef) {
      const isMuted = videoRef.muted;
      if (!isMuted) {
        handleVideoUnmute(videoRef); // Notify VideoManager
      }
      videoRef.muted = !isMuted; // Toggle mute state
    }
  };

  useEffect(() => {
    const handleVisibility = () => {
      const bounding = document
        .querySelector(".grid-media-layout")
        ?.getBoundingClientRect();
      if (
        bounding &&
        bounding.top >= 0 &&
        bounding.bottom <= window.innerHeight
      ) {
        setIsVisible(true);
      }
    };

    const handleResize = () => {
      if (useDynamicPadding) {
        const screenWidth = window.innerWidth;

        if (disablePaddingOnMobile && screenWidth < 600) {
          setDynamicPadding({ paddingLeft: 0, paddingRight: 0 });
        } else if (screenWidth <= 768) {
          const calculatedPadding = Math.min(
            (screenWidth / 768) * (maxPadding / 4),
            maxPadding
          );
          setDynamicPadding({
            paddingLeft: calculatedPadding,
            paddingRight: calculatedPadding,
          });
        } else if (screenWidth <= 1024) {
          const calculatedPadding = Math.min(
            (screenWidth / 1024) * (maxPadding / 2),
            maxPadding
          );
          setDynamicPadding({
            paddingLeft: calculatedPadding,
            paddingRight: calculatedPadding,
          });
        } else if (screenWidth <= 1500) {
          const calculatedPadding = Math.min(
            (screenWidth / 1500) * maxPadding,
            maxPadding
          );
          setDynamicPadding({
            paddingLeft: calculatedPadding,
            paddingRight: calculatedPadding,
          });
        } else {
          const calculatedPadding = Math.min(
            screenWidth * maxPadding,
            maxPadding
          );
          setDynamicPadding({
            paddingLeft: calculatedPadding,
            paddingRight: calculatedPadding,
          });
        }
      }
    };

    // Initial setup and event listeners
    handleVisibility();
    handleResize();

    window.addEventListener("scroll", handleVisibility);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleVisibility);
      window.removeEventListener("resize", handleResize);
    };
  }, [useDynamicPadding, maxPadding, disablePaddingOnMobile]);

  const getGridItemStyle = (row, col) => ({
    gridRow: row,
    gridColumn: col,
  });

  return (
    <div
      className="grid-media-layout"
      style={{
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        paddingLeft: `${
          useDynamicPadding ? dynamicPadding.paddingLeft : paddingLeft
        }px`,
        paddingRight: `${
          useDynamicPadding ? dynamicPadding.paddingRight : paddingRight
        }px`,
        gap: `${gap}px`,
      }}
    >
      {isVisible &&
        mediaItems.map((item, index) => (
          <div
            key={index}
            className="grid-media-item"
            style={getGridItemStyle(item.row, item.column)}
          >
            {item.type === "image" ? (
              <img
                src={item.src}
                alt={item.alt}
                className="grid-media-image"
                loading="lazy"
              />
            ) : (
              <video
                src={item.src}
                playsInline
                webkit-playsinline="true"
                controls
                className="grid-media-video"
                onClick={(e) => handleMuteToggle(e.target)}
                preload="metadata"
                onError={(e) => console.error("Video error:", e.target.error)}
              />
            )}
          </div>
        ))}
    </div>
  );
};

export default GridMediaLayout;
